import { useEffect, useMemo, useState } from "react"
import { formatDateRange } from "../../../utils/date.utils"
import {
  CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY,
  CLIENT_SPRINT_STATES,
} from "../../../consts/modules/clientReport.consts"
import {
  groupBlocksByCycle,
  groupSprintsByBlock,
} from "../../../services/clientReport.service"
import useToast from "../../../hooks/useToast.hook"
import {
  GENERIC_SAVE_ERROR_MESSAGE,
  GENERIC_SAVE_SUCCESS_MESSAGE,
} from "../../../consts/modules/vacations.consts"

const formatSprintsOptions = (clientSprints = []) => {
  return clientSprints
    .filter(
      (sprint) =>
        sprint.id > 0 &&
        sprint.sprintBlockId == null &&
        sprint.state !== CLIENT_SPRINT_STATES.IN_PROGRESS
    )
    .sort((a, b) => a.sprintNumber - b.sprintNumber)
    .map((sprint) => {
      let label = `Sprint ${sprint.sprintNumber}`
      if (sprint.startDate && sprint.endDate) {
        label += ` (${formatDateRange(sprint.startDate, sprint.endDate)})`
      }
      return {
        id: sprint.id,
        name: label,
        sprintNumber: sprint.sprintNumber,
      }
    })
}

const getNameNumericSegments = (name = "") => {
  const nameAsString = String(name)
  const match = nameAsString.match(/(\d+(?:\.\d+)*)/)

  if (!match?.[1]) {
    return []
  }

  return match[1]
    .split(".")
    .map((part) => Number(part))
    .filter((part) => Number.isFinite(part))
}

const compareNamesByNumericSegmentsAsc = (nameA = "", nameB = "") => {
  const numericPartsA = getNameNumericSegments(nameA)
  const numericPartsB = getNameNumericSegments(nameB)
  const maxLength = Math.max(numericPartsA.length, numericPartsB.length)

  for (let i = 0; i < maxLength; i++) {
    const partA = numericPartsA[i]
    const partB = numericPartsB[i]

    if (partA == null && partB != null) return -1
    if (partA != null && partB == null) return 1
    if (partA !== partB) return partA - partB
  }

  return String(nameA).localeCompare(String(nameB), "es")
}

const getBlockName = (block = {}) => {
  return block?.name || block?.sprintBlockName || block?.blockName || ""
}

const formatSprintBlocksOptions = (sprintBlocks = []) => {
  return sprintBlocks
    .filter((block) => {
      const id = Number(block?.id ?? block?.sprintBlockId)
      return Number.isFinite(id) && id > 0 && block?.sprintCycleId == null
    })
    .sort((a, b) =>
      compareNamesByNumericSegmentsAsc(getBlockName(a), getBlockName(b))
    )
    .map((block, index) => {
      const id = Number(block?.id ?? block?.sprintBlockId)
      const sprintCycleId = block?.sprintCycleId
      const sequence = index + 1
      let label =
        block?.name ||
        block?.sprintBlockName ||
        block?.blockName ||
        `Bloque ${sequence}`
      if (block.startDate && block.endDate) {
        label += ` (${formatDateRange(block.startDate, block.endDate)})`
      }
      return {
        id,
        sprintCycleId,
        name: label,
        sequence,
      }
    })
    .map((block, index) => ({
      ...block,
      sequence: index + 1,
    }))
}

const applyConsecutiveSelectionRule = ({
  options = [],
  selectedIds = [],
  sequenceKey,
}) => {
  const selectedIdsSet = new Set(selectedIds)

  const selectedSequenceValues = options
    .filter((option) => selectedIdsSet.has(option.id))
    .map((option) => option[sequenceKey])
    .filter((value) => value != null)

  if (selectedSequenceValues.length === 0) {
    return options.map((option) => ({ ...option, disabled: false }))
  }

  const minSelectedValue = Math.min(...selectedSequenceValues)
  const maxSelectedValue = Math.max(...selectedSequenceValues)

  return options.map((option) => {
    if (selectedIdsSet.has(option.id)) {
      return { ...option, disabled: false }
    }

    const optionSequenceValue = option[sequenceKey]
    const isAdjacentToSelection =
      optionSequenceValue === minSelectedValue - 1 ||
      optionSequenceValue === maxSelectedValue + 1

    return {
      ...option,
      disabled: !isAdjacentToSelection,
    }
  })
}

const isConsecutiveSelection = ({
  options = [],
  selectedIds = [],
  sequenceKey,
}) => {
  const selectedIdsSet = new Set(selectedIds)

  const selectedSequenceValues = options
    .filter((option) => selectedIdsSet.has(option.id))
    .map((option) => option[sequenceKey])
    .filter((value) => value != null)
    .sort((a, b) => a - b)

  if (selectedSequenceValues.length <= 1) return true

  for (let i = 1; i < selectedSequenceValues.length; i++) {
    if (selectedSequenceValues[i] - selectedSequenceValues[i - 1] !== 1) {
      return false
    }
  }

  return true
}

const ensureRequiredSelection = ({ selectedIds = [], requiredId }) => {
  if (requiredId == null) {
    return Array.isArray(selectedIds) ? selectedIds : []
  }

  const selectedIdsAsArray = Array.isArray(selectedIds) ? selectedIds : []
  const selectedIdsWithRequired = [requiredId, ...selectedIdsAsArray]
  const uniqueSelectedIds = []
  const seenIds = new Set()

  selectedIdsWithRequired.forEach((id) => {
    const normalizedId = Number(id)

    if (!Number.isFinite(normalizedId) || seenIds.has(normalizedId)) {
      return
    }

    seenIds.add(normalizedId)
    uniqueSelectedIds.push(normalizedId)
  })

  return uniqueSelectedIds
}

const normalizeSelectionByOptions = ({ selectedIds = [], options = [] }) => {
  const availableOptionIds = new Set(
    options
      .map((option) => Number(option?.id))
      .filter((id) => Number.isFinite(id))
  )

  return (Array.isArray(selectedIds) ? selectedIds : [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && availableOptionIds.has(id))
}

const useGroupClientSprints = ({
  clientSprints,
  clientId,
  triggerRefresh = () => {},
  sprintBlocks = [],
}) => {
  const { setErrorMessage, setSuccessMessage } = useToast()
  const initialFormState = {
    groupBy: CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY.BLOCK,
    selectedData: [],
    reportNotes: "",
  }
  const [formState, setFormState] = useState(initialFormState)

  const baseSprintsOptions = useMemo(() => {
    return formatSprintsOptions(clientSprints)
  }, [clientSprints])

  const baseSprintBlockOptions = useMemo(() => {
    return formatSprintBlocksOptions(sprintBlocks)
  }, [sprintBlocks])

  const sprintsOptions = useMemo(() => {
    return applyConsecutiveSelectionRule({
      options: baseSprintsOptions,
      selectedIds: formState.selectedData,
      sequenceKey: "sprintNumber",
    })
  }, [baseSprintsOptions, formState.selectedData])

  const sprintBlockOptions = useMemo(() => {
    return applyConsecutiveSelectionRule({
      options: baseSprintBlockOptions,
      selectedIds: formState.selectedData,
      sequenceKey: "sequence",
    })
  }, [baseSprintBlockOptions, formState.selectedData])

  const optionsByGroup = useMemo(() => {
    return formState.groupBy === CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY.BLOCK ?
        baseSprintsOptions
      : baseSprintBlockOptions
  }, [baseSprintsOptions, baseSprintBlockOptions, formState.groupBy])

  const requiredFirstId = useMemo(() => {
    return optionsByGroup[0]?.id
  }, [optionsByGroup])

  useEffect(() => {
    const normalizedSelectedIds = normalizeSelectionByOptions({
      selectedIds: formState.selectedData,
      options: optionsByGroup,
    })

    if (requiredFirstId == null) {
      if (
        normalizedSelectedIds.length === 0 &&
        formState.selectedData.length === 0
      ) {
        return
      }

      setFormState((prev) => ({
        ...prev,
        selectedData: [],
      }))
      return
    }

    const expectedSelection = ensureRequiredSelection({
      selectedIds: normalizedSelectedIds,
      requiredId: requiredFirstId,
    })
    const hasSelectionChanges =
      expectedSelection.length !== formState.selectedData.length ||
      expectedSelection.some(
        (id, index) => Number(formState.selectedData[index]) !== id
      )

    if (!hasSelectionChanges) {
      return
    }

    setFormState((prev) => ({
      ...prev,
      selectedData: expectedSelection,
    }))
  }, [formState.selectedData, optionsByGroup, requiredFirstId])

  const cleanUpFormState = () => setFormState(initialFormState)

  const handleChangeFormState = (field, value) => {
    if (field === "groupBy") {
      const targetOptions =
        value === CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY.BLOCK ?
          baseSprintsOptions
        : baseSprintBlockOptions
      const targetRequiredFirstId = targetOptions[0]?.id

      setFormState((prev) => ({
        ...prev,
        groupBy: value,
        selectedData: ensureRequiredSelection({
          selectedIds: [],
          requiredId: targetRequiredFirstId,
        }),
      }))
      return
    }

    if (field === "selectedData") {
      const nextSelectedIds = Array.isArray(value) ? value : []
      const optionsByCurrentGroup =
        formState.groupBy === CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY.BLOCK ?
          baseSprintsOptions
        : baseSprintBlockOptions
      const sequenceKeyByGroup =
        formState.groupBy === CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY.BLOCK ?
          "sprintNumber"
        : "sequence"
      const normalizedSelectedIds = normalizeSelectionByOptions({
        selectedIds: nextSelectedIds,
        options: optionsByCurrentGroup,
      })
      const selectionWithRequiredFirst = ensureRequiredSelection({
        selectedIds: normalizedSelectedIds,
        requiredId: requiredFirstId,
      })

      const isValidConsecutiveSelection = isConsecutiveSelection({
        options: optionsByCurrentGroup,
        selectedIds: selectionWithRequiredFirst,
        sequenceKey: sequenceKeyByGroup,
      })

      if (!isValidConsecutiveSelection) {
        return
      }

      value = selectionWithRequiredFirst
    }

    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSubmit = async () => {
    const selectedIds = formState.selectedData.map((id) => Number(id))

    const result =
      formState.groupBy === CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY.BLOCK ?
        await groupSprintsByBlock({
          sprintsIds: selectedIds,
          clientId,
          reportNotes: formState.reportNotes,
        })
      : await groupBlocksByCycle({
          blocksIds: selectedIds,
          clientId,
          reportNotes: formState.reportNotes,
        })

    if (!result.success) {
      return setErrorMessage(GENERIC_SAVE_ERROR_MESSAGE)
    }

    setSuccessMessage(GENERIC_SAVE_SUCCESS_MESSAGE)

    triggerRefresh()

    return result
  }

  const isSubmitDisabled = formState.selectedData.length === 0

  return {
    formState,
    handleChangeFormState,
    sprintsOptions,
    sprintBlockOptions,
    handleSubmit,
    isSubmitDisabled,
    cleanUpFormState,
  }
}

export default useGroupClientSprints
